import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import AddressField from '../../components/formfields/AddressField';
import clsx from 'clsx';
import { usePricingApi } from '../../hooks/usePricingApi';
import { useTheme } from '../../context/ThemeContext'; // Import the theme hook
import SolidMapPin from '../../components/SolidMapPin';
import LegCruiseSelection from './LegCruiseSelection';
import { useFormStore } from '../../stores/formData';
import { useServiceOptions, createServiceOptionsPayload } from '../../hooks/useServiceOptions';
import { calcNextShipDate } from '../../utils/dates';

const LegCard = ({ 
  legIndex,
  onAddressChange,
  onPricingChange,
  originRef,
  destinationRef,
}) => {
  // Use theme from context instead of prop
  const { theme } = useTheme();

  const actions = useFormStore((state) => state.actions);
  const helpers = useFormStore((state) => state.helpers);

  // Use direct selectors instead of derived state
  const origin = useFormStore((state) => state.formData.legs[legIndex]?.fromAddress);
  const destination = useFormStore((state) => state.formData.legs[legIndex]?.toAddress);
  const currentLeg = useFormStore.getState().formData.legs[legIndex];

  // Memoize addresses to prevent unnecessary reference changes
  const memoizedOrigin = useMemo(() => origin, [
    origin?.place_id,
    origin?.cruisePort?.id
  ]);
  
  const memoizedDestination = useMemo(() => destination, [
    destination?.place_id,
    destination?.cruisePort?.id
  ]);


  const handleSetOrigin = useCallback((origin) => {
    onAddressChange?.(legIndex, 'fromAddress', origin);
  },[legIndex, onAddressChange])

  const handleSetDestination = useCallback((destination) => {
    onAddressChange?.(legIndex, 'toAddress', destination);
  },[legIndex, onAddressChange])

  const handleCruiseToggle = useCallback((type, value) => {
    const otherType = type === 'pickup' ? 'delivery' : 'pickup'; 
    const cruiseSelection = currentLeg?.cruiseSelection || {};
    actions.leg.updateField(legIndex, 'cruiseSelection', {
      ...cruiseSelection,
      [type]: value,
      [otherType]: value ? false : cruiseSelection[otherType] || false // only one can be true at a time
    });
  }, [legIndex, actions]);

  // Theme-based styling
  const isDark = theme === 'dark';
  
  const cardClassName = clsx(
    'rounded-lg py-5 px-2 md:px-6',
    {
      'bg-slate-800 text-white': isDark,
      'bg-slate-200 text-gray-900': !isDark
    }
  );

    const sectionClassName = clsx(
    'px-2 md:px-3 mb-4',
    {
      'text-white': isDark,
      'bg-slate-200 text-gray-900': !isDark
    }
  );

  const sectionLabelClassName = clsx(
    'text-medium font-semibold',
    {
      'text-slate-300': isDark,
      'text-gray-800': !isDark
    }
  );

  const iconClassName = clsx(
    'text-lg',
    {
      'text-slate-400': isDark,
      'text-gray-500': !isDark
    }
  );

  const originPlaceHolder = useMemo(() => 
      memoizedOrigin ? `Enter your ${memoizedOrigin?.city || ''} Address` : 'Enter your origin address',
   [memoizedOrigin]);

  const destinationPlaceHolder = useMemo(() => 
      memoizedDestination ? `Enter your ${memoizedDestination?.city || ''} Address` : 'Enter your destination address',
  [memoizedDestination]);

  return (
    <div data-testid={`leg${legIndex}-card`} className={cardClassName}>
      <h3 className="text-xl font-semibold ml-3 mb-4">Origin and Destination</h3>
      <div className="space-y-6">
        {/* Origin Section */}
        <div className={sectionClassName} >
          <div className="flex items-center gap-2 mb-3">
            <SolidMapPin strokeWidth={4} size={18} className={iconClassName} />
            <span className={sectionLabelClassName}>Where from?</span>
          </div>
          <div className="relative flex flex-col justify-start gap-2">
            <label className="text-sm">Origin</label>
            <a ref={originRef} name="originRef"></a>
            <AddressField
              id={`leg${legIndex}-from-address`}
              placeholder={originPlaceHolder}
              types={['geocode']} //types={['locality','premise','establishment']}
              currentAddress={memoizedOrigin?.street ? memoizedOrigin : null}
              onAddressChange={handleSetOrigin}
              className="text-black"
              disabled={helpers.getCruiseSelection(legIndex, 'pickup')}
            />
          </div>
          <div className="flex items-center gap-2 mt-5">
            <LegCruiseSelection
              legIndex={legIndex}
              selectionType={'pickup'}
              address={memoizedOrigin}
              onCruiseToggle={handleCruiseToggle}
              cruiseSelection={helpers.getCruiseSelection(legIndex, 'pickup')}
            />
          </div>
        </div>

        {/* Destination Section */}
        <div className={sectionClassName}>
          <div className="flex items-center gap-2 mb-3">
            <SolidMapPin strokeWidth={4} size={18} className={iconClassName} />
            <span className={sectionLabelClassName}>Where to?</span>
          </div>
          <div className="relative flex flex-col justify-start gap-2">
            <label className="text-sm">Destination</label>
            <a ref={destinationRef} name="destinationRef"></a>
            <AddressField
              id={`leg${legIndex}-to-address`}
              placeholder={destinationPlaceHolder}
              types={['geocode']} //{['locality','premise','establishment']}
              currentAddress={memoizedDestination?.street ? memoizedDestination : null}
              onAddressChange={handleSetDestination}
              className="text-black"
              disabled={helpers.getCruiseSelection(legIndex, 'delivery')}
            />
          </div>
          <div className="flex items-center gap-2 mt-5">
            <LegCruiseSelection
              legIndex={legIndex}
              selectionType={'delivery'}
              address={memoizedDestination}
              onCruiseToggle={handleCruiseToggle}
              cruiseSelection={helpers.getCruiseSelection(legIndex, 'delivery')}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

LegCard.propTypes = {
  legIndex: PropTypes.number.isRequired,
  onRemove: PropTypes.func.isRequired,
  showRemove: PropTypes.bool.isRequired,
  onAddressChange: PropTypes.func.isRequired,
  onPricingChange: PropTypes.func.isRequired,
  totalPrice: PropTypes.number,
  originRef: PropTypes.object,
  destinationRef: PropTypes.object
};

export default LegCard;